import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { myCourses, type MyCourseSummary } from "../api/assignments";
import { ApiError } from "../api/client";
import { fileUrl } from "../api/files";
import { MobileTabBar } from "../components/MobileTabBar";

type TabKey = "current" | "completed";

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: "current", label: "Текущие" },
  { key: "completed", label: "Пройденные" },
];

export function MyCoursesPage() {
  const [items, setItems] = useState<MyCourseSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>("current");

  useEffect(() => {
    void myCourses()
      .then((r) => setItems(r.items))
      .catch((err) => {
        setError(
          err instanceof ApiError ? `Ошибка ${err.status}` : "Сеть недоступна",
        );
        setItems([]);
      });
  }, []);

  const filtered = useMemo(() => {
    if (!items) return [];
    return tab === "current"
      ? items.filter((c) => c.percent < 100)
      : items.filter((c) => c.percent === 100);
  }, [items, tab]);

  return (
    <div className="min-h-full bg-surface pb-20 md:pb-8">
      <header className="sticky top-0 z-10 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center px-4 py-3">
          <Link
            to="/"
            className="grid h-9 w-9 place-items-center rounded-lg text-ink-soft transition hover:bg-surface-alt"
            aria-label="Назад"
          >
            <ChevronLeft size={20} strokeWidth={2} />
          </Link>
          <h1 className="flex-1 text-center text-base font-semibold text-ink">
            Мои курсы
          </h1>
          <span className="h-9 w-9" aria-hidden />
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 pt-4">
        {/* Tabs */}
        <div className="mb-4 flex gap-2 rounded-xl bg-surface-alt p-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition ${
                tab === t.key
                  ? "bg-white text-ink shadow-sm"
                  : "text-ink-muted hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {items === null ? (
          <ListSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyTab tab={tab} />
        ) : (
          <ul className="space-y-3">
            {filtered.map((c) => (
              <li key={c.course_id}>
                <CourseCard course={c} />
              </li>
            ))}
          </ul>
        )}
      </main>

      <MobileTabBar active="courses" />
    </div>
  );
}

function ListSkeleton() {
  return (
    <ul className="space-y-3">
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className="h-32 animate-pulse rounded-2xl bg-surface-alt"
        />
      ))}
    </ul>
  );
}

function EmptyTab({ tab }: { tab: TabKey }) {
  return (
    <div className="card mt-2 p-8 text-center">
      <div className="text-base font-semibold text-ink">
        {tab === "current"
          ? "У вас пока нет активных курсов"
          : "Пройденных курсов пока нет"}
      </div>
      <p className="mt-1 text-sm text-ink-muted">
        {tab === "current"
          ? "Куратор назначит вам курсы в ближайшее время."
          : "Здесь появятся курсы после полного прохождения."}
      </p>
    </div>
  );
}

function courseSubtitle(c: MyCourseSummary): string {
  // Базовый курс — обязательный; иначе считаем маршрутным
  return c.is_mandatory ? "Базовый курс" : "Маршрут";
}

function CourseCard({ course }: { course: MyCourseSummary }) {
  return (
    <Link
      to={`/learn/courses/${course.course_id}`}
      className="group relative block overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-line transition hover:shadow-md"
    >
      {course.cover_file_id ? (
        <div className="relative aspect-[16/9] w-full">
          <img
            src={fileUrl(course.cover_file_id)}
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-4 text-white">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-base font-semibold leading-snug drop-shadow-sm">
                  {course.course_title}
                </div>
                <div className="mt-0.5 text-xs text-white/80">
                  {courseSubtitle(course)}
                </div>
              </div>
              <div className="shrink-0 rounded-full bg-white/20 px-2 py-0.5 text-xs font-semibold tabular-nums">
                {course.percent}%
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/25">
                <div
                  className={`h-full transition-all ${
                    course.percent === 100 ? "bg-brand-success" : "bg-white"
                  }`}
                  style={{ width: `${course.percent}%` }}
                />
              </div>
              <span className="shrink-0 text-[11px] text-white/85">
                {course.lessons_completed} из {course.lessons_total} уроков
              </span>
            </div>
          </div>
        </div>
      ) : (
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-base font-semibold leading-snug text-ink">
                {course.course_title}
              </div>
              <div className="mt-0.5 text-xs text-ink-muted">
                {courseSubtitle(course)}
              </div>
            </div>
            <div className="shrink-0 rounded-full bg-brand/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-brand">
              {course.percent}%
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-alt">
              <div
                className={`h-full rounded-full transition-all ${
                  course.percent === 100 ? "bg-brand-success" : "bg-brand"
                }`}
                style={{ width: `${course.percent}%` }}
              />
            </div>
            <span className="shrink-0 text-[11px] text-ink-muted">
              {course.lessons_completed} из {course.lessons_total} уроков
            </span>
          </div>
          {course.overdue && (
            <div className="mt-2">
              <span className="badge badge-danger">Просрочен</span>
            </div>
          )}
        </div>
      )}
    </Link>
  );
}
